import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Permission, Role, RoleListItem } from "../models";
import adminService from "services/adminService";
import {
  Box,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import MDButton from "components/MDButton";
import Icon from "@mui/material/Icon";
import { borderRadius } from "react-select/src/theme";

const RolesList = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState<RoleListItem[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [role, setRole] = useState<Role | null>();
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [selectedPermissionId, setSelectedPermissionId] = useState<number | null>(null);
  const [availablePermissions, setAvailablePermissions] = useState<Permission[]>([]);
  const [isAddRole, setIsAddRole] = useState<boolean>(false);
  const [roleName, setRoleName] = useState<string>('');

  useEffect(() => {
    adminService.getRoles().then((results) => {
      setRoles(results);
      if(results?.length>0)
        setSelectedRoleId(results[0].roleId);
    });
    adminService.getPermissions().then((results) => setPermissions(results));
  }, []);

  useEffect(() => {
    if (selectedRoleId) {
        adminService.getRole(selectedRoleId).then((results) => handleRole(results));
        setIsAddRole(false);
    }
  }, [selectedRoleId]);

  const handleRole=(resultRole:Role)=>{
    setAvailablePermissions(permissions.filter(c=>!resultRole.permissions?.some(r=> r.permissionName === c.permissionName )));
    setRole(resultRole);
  }
  const addRolePermission = () => {
    adminService.addRolePermission(selectedRoleId, selectedPermissionId).then(() =>
      adminService.getRole(selectedRoleId).then((results) => handleRole(results))
    );
    setSelectedPermissionId(null);
  };
  const removePermission = (permissionId:number) =>{
    adminService.removeRolePermission(selectedRoleId, permissionId).then(() =>
        adminService.getRole(selectedRoleId).then((results) => handleRole(results)))
  }
  const addRole = () => {
    adminService.addRole(roleName).then(() => {
      adminService.getRoles().then((results) => {
        setRoles(results);
        setRoleName('');
    })
    });
  };
  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} md={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  Roles{" "}
                  <Tooltip title="Add Role" sx={{verticalAlign:"bottom"}}>
                    <Icon fontSize="medium" 
                      onClick={() => {
                        setIsAddRole(true);
                        setSelectedRoleId(null);
                      }}
                    >
                      add_circle_outline
                    </Icon>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.length > 0 &&
                roles.map((role, index) => {
                  return (
                    <TableRow key={role.roleId}>
                      <TableCell
                        key={index}
                        sx={{ color:selectedRoleId === role.roleId ? "light.main": "", backgroundColor: selectedRoleId === role.roleId ? "secondary.main" : "", borderRadius: '0.375rem' }}
                        onClick={() => setSelectedRoleId(role.roleId!)}
                      >
                        {role.roleName}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </Grid>
        {selectedRoleId && (
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              {/* Membership lives on its own screen: the picker needs server-side search over
                  ~51k employees, which does not belong beside the permission chips. */}
              <Grid item xs={12} sx={{ mt: 2, ml: 2 }}>
                <MDButton
                  variant="contained"
                  color="dark"
                  onClick={() => navigate(`/admin/role/${selectedRoleId}/users`)}
                >
                  <Icon sx={{ mr: 1 }}>group</Icon>
                  Manage Users
                </MDButton>
              </Grid>
              <Grid item sx={{ m: 2 }}>
                {selectedRoleId &&
                  role &&
                  (role?.permissions?.length > 0 ? (
                    <>
                      <Typography sx={{ mr: 2 }}>Permissions:</Typography>
                      {role?.permissions.map((permission, index) => {
                        return (
                          <Chip
                            key={index}
                            sx={{ mr: 2, mt: 1 }}
                            label={permission.permissionName}
                            onDelete={() => removePermission(permission.permissionId!)}
                          />
                        );
                      })}
                    </>
                  ) : (
                    <Box>No Permissions have been added to this role.</Box>
                  ))}
              </Grid>
              <Grid item xs={12} sx={{ ml: 2 }}>
                <FormControl>
                  <InputLabel>Permissions</InputLabel>
                  <Select
                    id="permissions"
                    sx={{ minWidth: 210 }}
                    label="Permissions"
                    value={selectedPermissionId ?? ""}
                    onChange={(e) => setSelectedPermissionId(+e.target.value!)}
                  >
                    {availablePermissions &&
                      availablePermissions.map((c, index) => (
                        <MenuItem key={index} value={c.permissionId}>
                          {c.permissionName}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
                <MDButton sx={{ ml: 1 }} variant="contained" color="dark" onClick={addRolePermission}>
                  Add
                </MDButton>
              </Grid>
            </Grid>
          </Grid>
        )}
        {isAddRole && (
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              <Grid item xs={8}>
                <TextField
                  value={roleName}
                  inputProps={{ maxLength: 30 }}
                  label="Role Name"
                  fullWidth
                  onChange={(e) => {
                    setRoleName(e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={2}>
                <MDButton sx={{ ml: 1 }} variant="contained" color="dark" onClick={addRole}>
                  Add
                </MDButton>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default RolesList;
